// eslint-disable-next-line max-classes-per-file
const EventEmitter = require('events');
const ajax = require('component-ajax');
const queryString = require('query-string');
const eventsMixin = require('events-mixin');
const on = require('emmy/on');
const raf = require('component-raf');
const deepEqual = require('deep-equal');
const emit = require('emmy/emit');
const {
  merge,
} = require('../utils');

/**
 * Model contains global state shared between
 * all controllers and views.
 */
class Model extends EventEmitter {
  constructor(data = {}) {
    super();

    this.state = merge(data);

    on(window, 'popstate', (event) => {
      if (event.state) {
        this.update(() => event.state);
      }
    });

    this.setMaxListeners(100);
    this.viewsUpdated = true;
    this.requestParams = {};
  }

  /**
   * Check will be the model state changed by a function passed as parameter
   * @param  {Function} fn
   * @return {Boolean}  - will be model changed
   */
  willBeChanged(fn) {
    const prevState = this.state;
    const newState = fn(merge(this.state));

    return !deepEqual(prevState, newState);
  }

  /**
   * Update model state by fn.
   * Sends notification to views when animation frame available.
   *
   * @param fn takes old state and produces new one.
   */
  update(fn) {
    if (!this.willBeChanged(fn)) return;

    this.state = fn(merge(this.state));

    if (this.viewsUpdated) {
      this.viewsUpdated = false;
      raf(() => {
        this.viewsUpdated = true;
        this.emit('update', merge(this.state));
      });
    }
  }

  /**
   * Perform state change by update function,
   * then compute URL,
   * then make request,
   * then sync.
   *
   * @param update
   */
  updateWithSync(update, page = null, onSuccess = null) {
    if (!this.willBeChanged(update)) return;

    const prevState = this.state;
    const prevUrl = createUrl(prevState);

    this.update((state) => merge(update(state), Model.ajaxInProgress(true)));

    if (this.xhr) {
      this.xhr.abort();
    }

    if (page) {
      this.requestParams = {
        ...this.requestParams,
        ...(page !== 1 && { page }),
      };
    }
    this.xhr = ajax({
      url: createUrl(merge(this.state), createAjaxOptions(this.state, this.requestParams)),
      data: this.requestParams,
      success: (data) => {
        this.update((state) => {
          const newState = sync(data, state, prevState);
          this.emit('sync', merge(newState), merge(prevState));
          emit(document, 'feed-updated', null, true);

          const ajaxInProgress = Model.ajaxInProgress(false);
          return merge(newState, ajaxInProgress);
        });

        const nextState = this.state;
        const nextUrl = createUrl(nextState);
        const title = nextState.page.title || document.title;

        // do not manipulate with window history in case of item feed
        if (kudago && !kudago.isItemPage) {
          if (prevUrl != nextUrl) {
            if (!window.history.state) {
              window.history.replaceState(
                prevState,
                document.title,
                window.location,
              );
            }

            window.history.pushState(nextState, title, nextUrl);
          } else {
            window.history.replaceState(nextState, title, prevUrl);
          }
        }
        if (onSuccess) {
          onSuccess();
        }
      },
    });
  }

  /**
   * Generate state for ajax indicators.
   *
   * @param flag
   * @returns {{page: {ajaxInProgress: *}}}
   */
  static ajaxInProgress(flag) {
    return {
      page: {
        ajaxInProgress: flag,
      },
    };
  }
}

/**
 * Views must render their representation and
 * emit events to controllers.
 */
class View extends EventEmitter {
  constructor(model, element) {
    super();

    this.element = element || document.body;

    this.events = eventsMixin(this.element, this);

    model.on('update', (state) => this.render(state));
  }

  render() {
    throw new Error(`${this.constructor.name}::render() not implemented`);
  }
}

/**
 * Controllers can handle events, emitted by views,
 * and update models.
 * Handlers must have same name as view events.
 *
 * Additionally, all methods with name 'sync' will be
 * called after updateWithSync and produce new model
 * state from data and current/previous state.
 */
class Controller {
  constructor(model, view) {
    this.model = model;
    this.view = view;
    this.bindHandlers();
    this.registerSync();
  }

  bindHandlers() {
    const methodNames = Object.getOwnPropertyNames(this.constructor.prototype);
    const filter = (m) => Object.getOwnPropertyNames(Controller.constructor).indexOf(m) == -1;
    const handlers = methodNames.filter(filter);

    handlers.forEach((eventName) => {
      const handler = this[eventName];
      this.view.on(eventName, (...args) => {
        handler.apply(this, args);
      });
    });
  }

  registerSync() {
    Controller.syncFunctions = Controller.syncFunctions || [];
    if (this.constructor.prototype.sync) {
      Controller.syncFunctions.push(this.constructor.prototype.sync.bind(this));
    }
  }
}

/**
 * Create URL that can be placed in address bar.
 *
 * @param state
 * @param queryOpts
 * @returns {*}
 */
function createUrl(state, queryOpts = {}) {
  const facet = state.facetFilter || {};
  const navigation = state.navigation || {};
  const search = state.search || {};
  const filter = merge(facet, navigation, search);

  const path = [state.page.originUrl.slice(0, -1)];
  const query = merge(queryOpts, filter);
  if (state.page.urlFacetKey) {
    const values = filter[state.page.urlFacetKey];

    if (values && values.length == 1) {
      path.push(values[0]);
      delete query[state.page.urlFacetKey];
    }
  }

  const q = queryString.stringify(query);
  return `${path.join('/')}/${q ? `?${q}` : ''}`;
}

/**
 * Create additional options for ajax request.
 *
 * @param state
 * @param requestParams
 * @returns {{get_facets: 1}}
 */
function createAjaxOptions(state, requestParams) {
  const result = requestParams.from_item ? {} : { get_facets: 1 };
  if (state.page.index && state.page.index !== 1) {
    result.page = state.page.index;
  }

  return result;
}

/**
 * Compose all 'sync' methods and apply resulting transformation
 * to produce new state.
 *
 * @param data
 * @param state
 * @param prevState
 * @returns {*}
 */
function sync(data, state, prevState) {
  const transformers = Controller.syncFunctions || [(d, s) => s];
  const reducer = (prev, next) => (dt, st, prevSt) => next(dt, prev(dt, st, prevSt), prevSt);
  const syncFn = transformers.reduce(reducer);

  return syncFn(data, state, prevState);
}

module.exports = {
  Model,
  View,
  Controller,
  createUrl,
};
